import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, FileText, ListChecks, ClipboardList, Trash2, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, PageState } from "@/components/app/Common";
import { DocViewer } from "@/components/app/DocViewer";
import { useLoad } from "@/hooks/useLoad";
import { API, api, errMsg } from "@/lib/api";
import { Sources, useResearchStatus, ResearchGate, ConfidenceBadge } from "@/components/app/Research";

const SUGGESTED = ["What filings are due?", "Why is my score dropping?", "What receipts are uncategorized?", "What do I need for a revival?", "What taxes are overdue?"];
const GEN = [["compliance_report", "Compliance Report", FileText], ["filing_summary", "Filing Summary", ClipboardList], ["action_list", "Action List", ListChecks]];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-primary text-primary-foreground" : "surface"}`}>
      {m.role === "user" ? <p className="text-sm whitespace-pre-wrap">{m.content}</p> : <div className="prose-aq overflow-x-auto"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || "…"}</ReactMarkdown>{m.confidence && <div className="mt-2"><ConfidenceBadge c={m.confidence} testId="chat-confidence" /></div>}<Sources sources={m.sources} testId="chat-sources" /></div>}
    </div>
  </div>
);

export default function Assistant() {
  const state = useLoad(() => api.get("/ai/history").then((r) => r.data));
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(null);
  const [doc, setDoc] = useState(null);
  const end = useRef();
  const [web, setWeb] = useState(false);
  const research = useResearchStatus();
  const webReady = !!(research.data?.configured && research.data?.features?.assistant);
  useEffect(() => {
    if (state.data) setMsgs(state.data);
  }, [state.data]);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("aq_token") || ""}` },
        body: JSON.stringify({ message: q, web: web && webReady }),
      });
      if (!res.ok) throw new Error((await res.json()).detail?.message || "Assistant unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          const ev = JSON.parse(line);
          if (ev.error) toast.error(ev.error);
          if (ev.notice) toast.info(ev.notice);
          if (ev.sources) setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], sources: ev.sources, confidence: ev.confidence }]);
          if (ev.delta) setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], role: "assistant", content: m[m.length - 1].content + ev.delta }]);
        }
      }
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };
  const generate = async (kind) => {
    setGen(kind);
    try {
      setDoc((await api.post("/ai/generate", { kind })).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGen(null);
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Module 9" title="AI corporate assistant" subtitle="Answers grounded in your live filings, taxes, receipts and entity records.">
        <Button variant="ghost" size="sm" onClick={async () => { await api.delete("/ai/history"); setMsgs([]); }} data-testid="clear-chat-btn"><Trash2 className="h-4 w-4 mr-1" />Clear</Button>
      </PageHeader>
      <PageState state={state}>
        <div className="grid lg:grid-cols-[1fr_280px] gap-6">
          <div className="surface flex flex-col h-[calc(100vh-260px)] min-h-[480px]">
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
              {msgs.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="h-5 w-5" /></div>
                  <p className="mt-3 font-semibold">Ask anything about your corporations</p>
                  <div className="mt-5 flex flex-wrap gap-2 justify-center max-w-lg">
                    {SUGGESTED.map((s) => <button key={s} onClick={() => send(s)} className="text-sm rounded-full border border-border px-3 py-1.5 hover:bg-accent" data-testid={`suggested-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</button>)}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => <Bubble key={i} m={m} />)}
              {busy && msgs[msgs.length - 1]?.content === "" && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Analyzing your records…</div>}
              <div ref={end} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 flex flex-wrap gap-2">
              <div className="w-full flex items-center gap-2 text-xs">
                <button type="button" onClick={() => webReady && setWeb(!web)} disabled={!webReady} data-testid="chat-web-toggle"
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 transition-colors ${web ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"} disabled:opacity-60`}><Globe className="h-3.5 w-3.5" />Search the web{web && webReady ? ": on" : ""}</button>
                <ResearchGate status={research} feature="assistant" testId="chat-web-not-configured" />
              </div>
              <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask: What do I need for a revival?" className="resize-none min-h-[44px] flex-1"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="chat-input" />
              <Button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
            </form>
          </div>
          <div className="space-y-4">
            <div className="surface p-5">
              <div className="font-semibold mb-3">Generate</div>
              <div className="space-y-2">
                {GEN.map(([k, l, Icon]) => (
                  <Button key={k} variant="outline" className="w-full justify-start" disabled={!!gen} onClick={() => generate(k)} data-testid={`gen-${k}`}>
                    {gen === k ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icon className="h-4 w-4 mr-2" />}{l}
                  </Button>
                ))}
              </div>
            </div>
            <div className="surface p-5 text-xs text-muted-foreground">The assistant provides guidance, not legal or tax advice. Confirm filings with the registry or your advisor.</div>
          </div>
        </div>
      </PageState>
      <DocViewer doc={doc} onClose={() => setDoc(null)} />
    </div>
  );
}
